/**
 * CRA3D Resume Studio — Editor shell.
 *
 * Section tabs on the left / top (mobile), claim list on the right.
 * Each claim carries a provenance status pill (per Mark's Phase 2
 * authorization: NO "AI draft" pill in Phase 2 — the AI-draft flow
 * lands in Phase 4). Preview / Download are hidden by design in this
 * phase — Phase 5.
 *
 * The traceability invariant is enforced server-side: the "Approve
 * section" button POSTs to the backend, which validates every claim's
 * source is alive and every claim is approved. If the server returns
 * 400 with `unapproved_claim_ids` or `invalidated_claim_ids`, the UI
 * highlights the offending claim rows.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";
import {
  FileText, ArrowLeft, Check, X, RefreshCw, DownloadCloud, PenLine, Lock,
  CircleAlert, CheckCircle2, ShieldCheck, Plus, Trash2, Upload,
  Sparkles, Target, Info, History,
} from "lucide-react";

const SECTION_LABEL = {
  summary:              "Summary",
  employment_history:   "Employment history",
  education:            "Education",
  skills_technical:     "Technical skills",
  skills_transferable:  "Transferable skills",
  projects:             "Projects",
  certifications:       "Certifications",
  contact:              "Contact",
  links:                "Links",
};

const CRA3D_KINDS = new Set([
  "summary", "skills_transferable", "projects", "skills_technical",
]);

function ProvenanceChip({ claim }) {
  const p = claim.provenance;
  const approved = claim.approved_by_user;
  const alive = claim.source_alive !== false;
  if (!alive) {
    return (
      <Badge
        variant="destructive"
        data-testid={`claim-chip-invalid-${claim.claim_id}`}
        className="uppercase text-[10px] tracking-wider"
      >
        <CircleAlert className="h-3 w-3 mr-1" aria-hidden="true" />
        Source removed
      </Badge>
    );
  }
  if (p === "user_entered") {
    return (
      <Badge
        data-testid={`claim-chip-user-${claim.claim_id}`}
        className="uppercase text-[10px] tracking-wider bg-progress-teal/20 text-progress-teal border border-progress-teal/40"
      >
        <PenLine className="h-3 w-3 mr-1" aria-hidden="true" />
        You entered this
      </Badge>
    );
  }
  if (p === "user_uploaded") {
    return (
      <Badge
        data-testid={`claim-chip-uploaded-${claim.claim_id}`}
        className="uppercase text-[10px] tracking-wider bg-progress-teal/20 text-progress-teal border border-progress-teal/40"
      >
        <PenLine className="h-3 w-3 mr-1" aria-hidden="true" />
        You uploaded this
      </Badge>
    );
  }
  if (p === "cra3d_approved") {
    return approved ? (
      <Badge
        data-testid={`claim-chip-cra3d-approved-${claim.claim_id}`}
        className="uppercase text-[10px] tracking-wider bg-accent-warm/20 text-accent-warm border border-accent-warm/40"
      >
        <CheckCircle2 className="h-3 w-3 mr-1" aria-hidden="true" />
        From CRA3D · approved
      </Badge>
    ) : (
      <Badge
        data-testid={`claim-chip-cra3d-needs-review-${claim.claim_id}`}
        className="uppercase text-[10px] tracking-wider bg-bg-surface-hover text-fg-primary border border-border-subtle"
      >
        <ShieldCheck className="h-3 w-3 mr-1" aria-hidden="true" />
        From CRA3D · needs review
      </Badge>
    );
  }
  return null;
}

function ClaimRow({ claim, onApprove, onReject, onDelete, busy }) {
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [reason, setReason] = useState("");  return (
    <li
      data-testid={`claim-row-${claim.claim_id}`}
      data-provenance={claim.provenance}
      data-approved={claim.approved_by_user ? "true" : "false"}
      data-status={claim.status}
      className={
        "rounded-md border p-3 " +
        (claim.status === "rejected"
          ? "border-border-subtle/40 bg-bg-surface/40 opacity-60"
          : claim.source_alive === false
          ? "border-red-500/50 bg-red-500/5"
          : "border-border-subtle bg-bg-surface")
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <ProvenanceChip claim={claim} />
        {claim.status === "rejected" && (
          <span className="text-[10px] uppercase tracking-wider text-fg-muted">
            Rejected
          </span>
        )}
      </div>
      <p className="mt-2 text-sm text-fg-primary whitespace-pre-line"
         data-testid={`claim-row-${claim.claim_id}-text`}>
        {claim.text}
      </p>
      {claim.reject_reason && (
        <p className="mt-1 text-[11px] text-fg-muted italic">
          Reason: {claim.reject_reason}
        </p>
      )}
      {claim.status === "active" && claim.provenance === "cra3d_approved" && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!claim.approved_by_user && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onApprove(claim.claim_id)}
              disabled={busy || claim.source_alive === false}
              data-testid={`claim-row-${claim.claim_id}-approve-btn`}
              className="text-progress-teal hover:text-progress-teal hover:bg-progress-teal/10"
            >
              <Check className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Approve
            </Button>
          )}
          {!showRejectReason ? (
            <Button
              size="sm" variant="ghost"
              onClick={() => setShowRejectReason(true)}
              disabled={busy}
              data-testid={`claim-row-${claim.claim_id}-reject-btn`}
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Reject
            </Button>
          ) : (
            <div className="w-full space-y-2">
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional reason so you remember why you rejected it."
                maxLength={400}
                data-testid={`claim-row-${claim.claim_id}-reject-reason`}
                className="bg-bg-base border-border-subtle text-fg-primary min-h-16"
              />
              <VoiceInputButton
                fieldTestid={`claim-row-${claim.claim_id}-reject-voice-btn`}
                ariaLabel="Speak your rejection reason"
                onTranscript={(t) => setReason(appendTranscript(reason, t))}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => { onReject(claim.claim_id, reason); setShowRejectReason(false); setReason(""); }}
                  disabled={busy}
                  data-testid={`claim-row-${claim.claim_id}-reject-confirm-btn`}
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  Confirm reject
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => { setShowRejectReason(false); setReason(""); }}
                  data-testid={`claim-row-${claim.claim_id}-reject-cancel-btn`}
                  className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {claim.status === "active" && claim.provenance === "user_entered" && onDelete && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm" variant="ghost"
            onClick={() => onDelete(claim.claim_id)}
            disabled={busy}
            data-testid={`claim-row-${claim.claim_id}-remove-btn`}
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Remove
          </Button>
        </div>
      )}
    </li>
  );
}

export default function ResumeStudioEditor() {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveNotice, setApproveNotice] = useState(null);
  const [invalidatedIds, setInvalidatedIds] = useState([]);
  const [unapprovedIds, setUnapprovedIds] = useState([]);
  const [editorText, setEditorText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [showTargetJob, setShowTargetJob] = useState(false);
  const [tjDescription, setTjDescription] = useState("");
  const [tjTitle, setTjTitle] = useState("");
  const [tjOrg, setTjOrg] = useState("");
  const [tjBusy, setTjBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get(`/resume-studio/resumes/${resumeId}`),
        api.get(`/resume-studio/resumes/${resumeId}/sections`),
      ]);
      setResume(r.resume);
      setSections(s.sections || []);
      if (!activeSectionId && (s.sections || []).length) {
        setActiveSectionId(s.sections[0].id);
      }
    } catch (err) {
      setError(extractApiError(err, "Failed to load resume."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resumeId]);

  const activeSection = useMemo(
    () => sections.find((s) => s.id === activeSectionId) || null,
    [sections, activeSectionId],
  );

  const canImport = activeSection && CRA3D_KINDS.has(activeSection.section_kind);

  const doImport = async () => {
    if (!activeSection) return;
    setBusy(true);
    setError(null); setApproveNotice(null); setInvalidatedIds([]); setUnapprovedIds([]);
    try {
      const { data } = await api.post(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/import-from-cra3d`,
      );
      toast.success(`${data.imported_count} claim(s) imported. Review each one before approving the section.`);
      await load();
    } catch (err) {
      setError(extractApiError(err, "Import failed."));
    } finally {
      setBusy(false);
    }
  };

  const doApproveClaim = async (claim_id) => {
    setBusy(true);
    try {
      await api.post(`/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/claims/${claim_id}/approve`);
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Approve failed."));
    } finally { setBusy(false); }
  };

  const doRejectClaim = async (claim_id, reason) => {
    setBusy(true);
    try {
      await api.post(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/claims/${claim_id}/reject`,
        { reason: reason || null },
      );
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Reject failed."));
    } finally { setBusy(false); }
  };

  const doAddUserEntry = async () => {
    if (!editorText.trim() || !activeSection) return;
    setBusy(true);
    try {
      const existingUserEntries = (activeSection.claims || [])
        .filter((c) => c.provenance === "user_entered" && c.status === "active")
        .map((c) => ({ text: c.text, claim_id: c.claim_id }));
      const nextClaims = [...existingUserEntries, { text: editorText.trim() }];
      await api.put(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}`,
        { claims: nextClaims },
      );
      setEditorText("");
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Save failed."));
    } finally { setBusy(false); }
  };

  const doRemoveUserEntry = async (claim_id) => {
    setBusy(true);
    try {
      const remaining = (activeSection.claims || [])
        .filter((c) => c.provenance === "user_entered"
                       && c.status === "active" && c.claim_id !== claim_id)
        .map((c) => ({ text: c.text, claim_id: c.claim_id }));
      await api.put(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}`,
        { claims: remaining },
      );
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Remove failed."));
    } finally { setBusy(false); }
  };

  const doApproveSection = async () => {
    if (!activeSection) return;
    setBusy(true);
    setError(null); setApproveNotice(null); setInvalidatedIds([]); setUnapprovedIds([]);
    try {
      await api.post(`/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/approve`);
      toast.success("Section approved.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail) {
        setApproveNotice(detail.message);
        setUnapprovedIds(detail.unapproved_claim_ids || []);
        setInvalidatedIds(detail.invalidated_claim_ids || []);
        await load();
      } else {
        setError(extractApiError(err, "Approve failed."));
      }
    } finally { setBusy(false); }
  };

  const doAiDraft = async () => {
    if (!activeSection) return;
    setAiBusy(true); setAiError(null);
    try {
      const { data } = await api.post(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/ai-draft`,
      );
      setAiUsage(data.usage || null);
      toast.success("AI draft generated. Review before using.");
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && typeof detail === "object" && detail?.unsupported) {
        // Structured validator rejection — surface the specific facts.
        const parts = [];
        for (const [k, vals] of Object.entries(detail.unsupported)) {
          const preview = (vals || []).slice(0, 3).join(", ");
          parts.push(`${k}: ${preview}${vals.length > 3 ? " …" : ""}`);
        }
        setAiError(
          "The AI introduced information we couldn't trace to your "
          + "approved evidence. Draft was rejected. — "
          + parts.join("; "),
        );
      } else {
        setAiError(extractApiError(err, "AI drafting failed."));
      }
    } finally { setAiBusy(false); }
  };

  const doRejectAiDraft = async () => {
    if (!activeSection) return;
    setAiBusy(true);
    try {
      await api.delete(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/ai-draft`,
      );
      setAiUsage(null); setAiError(null);
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Discard failed."));
    } finally { setAiBusy(false); }
  };

  const doAcceptAiDraftAsClaim = async () => {
    if (!activeSection || !activeSection.ai_draft_text) return;
    setAiBusy(true);
    try {
      // Convert the draft into a user_entered claim — the customer
      // has now read it, edited it (or not), and taken responsibility.
      // Existing user_entered claims are preserved; only appending.
      const existing = (activeSection.claims || [])
        .filter((c) => c.provenance === "user_entered" && c.status === "active")
        .map((c) => ({ text: c.text, claim_id: c.claim_id }));
      await api.put(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}`,
        { claims: [...existing, { text: activeSection.ai_draft_text }] },
      );
      // Clear the draft slot now that it's landed as a real claim.
      await api.delete(
        `/resume-studio/resumes/${resumeId}/sections/${activeSection.id}/ai-draft`,
      );
      setAiUsage(null); setAiError(null);
      toast.success("Draft accepted as an approved entry.");
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Accept failed."));
    } finally { setAiBusy(false); }
  };

  const attachTargetJob = async () => {
    if (!tjDescription.trim()) return;
    setTjBusy(true);
    try {
      await api.post(`/resume-studio/resumes/${resumeId}/target-job`, {
        description: tjDescription.trim(),
        title: tjTitle.trim() || null,
        org:   tjOrg.trim() || null,
      });
      toast.success("Target job attached.");
      setShowTargetJob(false);
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Attach failed."));
    } finally { setTjBusy(false); }
  };

  const clearTargetJob = async () => {
    setTjBusy(true);
    try {
      await api.delete(`/resume-studio/resumes/${resumeId}/target-job`);
      toast.success("Target job cleared.");
      await load();
    } catch (err) {
      toast.error(extractApiError(err, "Clear failed."));
    } finally { setTjBusy(false); }
  };

  if (loading) {
    return <AppShell>
      <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
         data-testid="resume-studio-editor-loading">Loading…</p>
    </AppShell>;
  }

  if (!resume) {
    return <AppShell>
      <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
         data-testid="resume-studio-editor-missing">
        This resume is not available.
      </p>
    </AppShell>;
  }

  const activeClaims = activeSection?.claims || [];
  const userEntries = activeClaims.filter(
    (c) => c.provenance === "user_entered" && c.status === "active");
  const cra3dEntries = activeClaims.filter(
    (c) => c.provenance === "cra3d_approved" && c.status === "active");
  const rejected = activeClaims.filter((c) => c.status === "rejected");
  const isApproved = activeSection?.status === "approved";

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
           data-testid="resume-studio-editor">
        <button
          onClick={() => navigate("/resume-studio")}
          data-testid="resume-studio-editor-back"
          className="mb-4 text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to your resumes
        </button>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">
              Resume Studio
            </p>
            <h1
              data-testid="resume-studio-editor-title"
              className="mt-1 text-2xl sm:text-3xl font-heading font-medium text-fg-primary flex items-center gap-2"
            >
              <FileText className="h-5 w-5" aria-hidden="true" />
              {resume.name}
            </h1>
            <p className="mt-1 text-xs text-fg-muted">
              {resume.resume_type.replace(/_/g, " ")} · {resume.starting_method.replace(/_/g, " ")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate(`/resume-studio/${resumeId}/upload`)}
              data-testid="resume-studio-editor-upload-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              <Upload className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Upload / paste
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(`/resume-studio/${resumeId}/versions`)}
              data-testid="resume-studio-editor-versions-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              <History className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Versions
            </Button>
            <Button
              onClick={() => navigate(`/resume-studio/${resumeId}/preview`)}
              data-testid="resume-studio-editor-preview-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              <FileText className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Preview &amp; export
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="resume-studio-editor-error"
                  className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <nav
            data-testid="resume-studio-editor-tabs"
            className="lg:sticky lg:top-4 self-start rounded-lg border border-border-subtle bg-bg-surface p-2"
            aria-label="Sections"
          >
            <ul className="flex lg:flex-col gap-1 overflow-x-auto">
              {sections.map((s) => {
                const isActive = s.id === activeSectionId;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setActiveSectionId(s.id)}
                      data-testid={`resume-studio-editor-tab-${s.section_kind}`}
                      data-status={s.status}
                      className={
                        "w-full text-left text-sm px-3 py-2 rounded-md transition-colors duration-150 " +
                        (isActive
                          ? "bg-bg-surface-hover text-fg-primary"
                          : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover")
                      }
                    >
                      <span className="flex items-center gap-2">
                        {SECTION_LABEL[s.section_kind] || s.section_kind}
                        {s.status === "approved" && (
                          <CheckCircle2 className="h-3.5 w-3.5 text-progress-teal" aria-hidden="true" />
                        )}
                        {s.status === "user_edited" && (
                          <span className="inline-block h-2 w-2 rounded-full bg-accent-warm" aria-hidden="true" />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <section
            data-testid={`resume-studio-editor-section-${activeSection?.section_kind || "none"}`}
            className="rounded-lg border border-border-subtle bg-bg-surface p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Section</p>
                <h2 className="mt-1 text-xl font-heading font-medium text-fg-primary">
                  {activeSection ? SECTION_LABEL[activeSection.section_kind] : "—"}
                </h2>
                <p className="mt-1 text-xs text-fg-muted">
                  {activeClaims.length} claim(s) · {userEntries.length} from you · {cra3dEntries.length} from CRA3D
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canImport && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={doImport}
                    disabled={busy}
                    data-testid="resume-studio-editor-import-btn"
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                    Import from CRA3D
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={doAiDraft}
                  disabled={aiBusy || activeClaims.filter((c) => c.status === "active").length === 0}
                  data-testid="resume-studio-editor-ai-draft-btn"
                  className="text-accent-warm hover:text-accent-warm hover:bg-accent-warm/10"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                  {aiBusy ? "Drafting…" : "Draft with AI"}
                </Button>
                <Button
                  size="sm"
                  onClick={doApproveSection}
                  disabled={busy || activeClaims.length === 0}
                  data-testid="resume-studio-editor-approve-section-btn"
                  className={
                    isApproved
                      ? "bg-progress-teal/20 text-progress-teal border border-progress-teal/40 hover:bg-progress-teal/20"
                      : "bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                  }
                >
                  {isApproved ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      Section approved
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      Approve section
                    </>
                  )}
                </Button>
              </div>
            </div>

            {approveNotice && (
              <Alert
                variant="default"
                data-testid="resume-studio-editor-approve-notice"
                className="mt-4 border-accent-warm/40 bg-accent-warm/5 text-fg-primary"
              >
                <AlertDescription>
                  {approveNotice}
                  {unapprovedIds.length > 0 && (
                    <span className="block mt-2 text-xs text-fg-secondary">
                      {unapprovedIds.length} claim(s) waiting for your approval.
                    </span>
                  )}
                  {invalidatedIds.length > 0 && (
                    <span className="block mt-2 text-xs text-fg-secondary">
                      {invalidatedIds.length} claim(s) whose CRA3D source has been
                      rejected/removed since import. Confirm or remove each one.
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {aiError && (
              <Alert
                variant="destructive"
                data-testid="resume-studio-editor-ai-error"
                className="mt-4"
              >
                <AlertDescription>{aiError}</AlertDescription>
              </Alert>
            )}

            {activeSection?.ai_draft_text && (
              <div
                data-testid="resume-studio-editor-ai-draft-panel"
                className="mt-5 rounded-md border border-accent-warm/40 bg-accent-warm/5 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-accent-warm flex items-center gap-1">
                      <Sparkles className="h-3 w-3" aria-hidden="true" />
                      AI draft — not on your resume yet
                    </p>
                    <p className="mt-1 text-xs text-fg-secondary">
                      Only a suggestion. Your approved entries below are
                      unchanged. Accept to add the draft as your own entry,
                      or discard.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={doAcceptAiDraftAsClaim}
                      disabled={aiBusy}
                      data-testid="resume-studio-editor-ai-accept-btn"
                      className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                    >
                      <Check className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                      Accept as my entry
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={doRejectAiDraft}
                      disabled={aiBusy}
                      data-testid="resume-studio-editor-ai-discard-btn"
                      className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                    >
                      <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                      Discard
                    </Button>
                  </div>
                </div>
                <p
                  data-testid="resume-studio-editor-ai-draft-text"
                  className="mt-3 text-sm text-fg-primary leading-relaxed whitespace-pre-line"
                >
                  {activeSection.ai_draft_text}
                </p>
                {(activeSection.missing_info_prompts || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-accent-warm/20"
                        data-testid="resume-studio-editor-ai-missing-info">
                    <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-1">
                      <Info className="h-3 w-3" aria-hidden="true" />
                      Questions the draft flagged for you
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-fg-secondary list-disc pl-5">
                      {activeSection.missing_info_prompts.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {aiUsage && (
                  <p className="mt-3 text-[10px] text-fg-muted">
                    AI usage: {aiUsage.input_tokens} in / {aiUsage.output_tokens} out
                    · {aiUsage.latency_ms} ms
                    · ≈${aiUsage.estimated_cost?.toFixed(4)}
                  </p>
                )}
              </div>
            )}

            {resume?.resume_type === "targeted_ats" && (
              <div
                data-testid="resume-studio-editor-target-job"
                className="mt-5 rounded-md border border-border-subtle bg-bg-surface p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-1">
                      <Target className="h-3 w-3" aria-hidden="true" />
                      Target job for this resume
                    </p>
                    {resume.target_job ? (
                      <>
                        <p className="mt-1 text-sm text-fg-primary">
                          <strong>{resume.target_job.title || "Untitled role"}</strong>
                          {resume.target_job.org && ` — ${resume.target_job.org}`}
                        </p>
                        <p className="mt-1 text-xs text-fg-muted line-clamp-3">
                          {resume.target_job.description?.slice(0, 240)}
                          {resume.target_job.description?.length > 240 ? "…" : ""}
                        </p>
                      </>
                    ) : (
                      <p className="mt-1 text-xs text-fg-secondary">
                        Paste a job description to tailor future AI drafts.
                        JD text is never copied onto your resume.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {resume.target_job && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={clearTargetJob}
                        disabled={tjBusy}
                        data-testid="resume-studio-editor-target-job-clear-btn"
                        className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        setShowTargetJob((v) => !v);
                        setTjDescription(resume.target_job?.description || "");
                        setTjTitle(resume.target_job?.title || "");
                        setTjOrg(resume.target_job?.org || "");
                      }}
                      data-testid="resume-studio-editor-target-job-toggle-btn"
                      className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                    >
                      {resume.target_job ? "Edit" : "Add"}
                    </Button>
                  </div>
                </div>
                {showTargetJob && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="text"
                      placeholder="Job title (optional)"
                      value={tjTitle}
                      maxLength={200}
                      onChange={(e) => setTjTitle(e.target.value)}
                      data-testid="resume-studio-editor-target-job-title"
                      className="w-full text-sm rounded-md bg-bg-base border border-border-subtle text-fg-primary px-3 py-2"
                    />
                    <input
                      type="text"
                      placeholder="Organization (optional)"
                      value={tjOrg}
                      maxLength={200}
                      onChange={(e) => setTjOrg(e.target.value)}
                      data-testid="resume-studio-editor-target-job-org"
                      className="w-full text-sm rounded-md bg-bg-base border border-border-subtle text-fg-primary px-3 py-2"
                    />
                    <Textarea
                      placeholder="Paste the job description here (guidance for AI drafts — never copied to your resume verbatim)"
                      value={tjDescription}
                      maxLength={12000}
                      onChange={(e) => setTjDescription(e.target.value)}
                      data-testid="resume-studio-editor-target-job-description"
                      className="min-h-32 bg-bg-base border-border-subtle text-fg-primary text-xs"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setShowTargetJob(false)}
                        disabled={tjBusy}
                        className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                        data-testid="resume-studio-editor-target-job-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={attachTargetJob}
                        disabled={tjBusy || tjDescription.trim().length < 10}
                        data-testid="resume-studio-editor-target-job-save-btn"
                        className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                      >
                        {tjBusy ? "Saving…" : "Save target job"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Claim list */}
            <div className="mt-5 space-y-4">
              {activeClaims.length === 0 ? (
                <p className="text-sm text-fg-secondary" data-testid="resume-studio-editor-empty-section">
                  This section is empty. {canImport
                    ? "Import approved content from CRA3D above, or type your own below."
                    : "Type an entry below."}
                </p>
              ) : (
                <ul className="space-y-2" data-testid="resume-studio-editor-claims">
                  {activeClaims.filter((c) => c.status === "active").map((c) => (
                    <ClaimRow
                      key={c.claim_id}
                      claim={c}
                      onApprove={doApproveClaim}
                      onReject={doRejectClaim}
                      onDelete={c.provenance === "user_entered" ? doRemoveUserEntry : null}
                      busy={busy}
                    />
                  ))}
                </ul>
              )}
              {rejected.length > 0 && (
                <details className="pt-2">
                  <summary className="text-xs text-fg-muted cursor-pointer"
                            data-testid="resume-studio-editor-rejected-summary">
                    {rejected.length} rejected claim(s) — kept for history
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {rejected.map((c) => (
                      <ClaimRow key={c.claim_id} claim={c} busy={busy} />
                    ))}
                  </ul>
                </details>
              )}
            </div>

            {/* User-entered add box */}
            <div className="mt-6 border-t border-border-subtle pt-4">
              <p className="text-sm font-medium text-fg-primary flex items-center gap-2">
                <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                Add an entry you typed yourself
              </p>
              <p className="mt-1 text-xs text-fg-secondary">
                You author it, so it starts approved. You can still remove it later.
              </p>
              <Textarea
                value={editorText}
                onChange={(e) => setEditorText(e.target.value)}
                maxLength={2000}
                data-testid="resume-studio-editor-user-input"
                className="mt-3 bg-bg-base border-border-subtle text-fg-primary min-h-24"
                placeholder={
                  activeSection?.section_kind === "employment_history"
                    ? "e.g., \"Springfield Public Library — Circulation Clerk, 2018–2023: Ran the front desk and trained two new clerks.\""
                    : "Type one entry per line."
                }
              />
              <div className="mt-2">
                <VoiceInputButton
                  fieldTestid="resume-studio-editor-user-input-voice-btn"
                  ariaLabel="Speak this entry"
                  onTranscript={(t) => setEditorText(appendTranscript(editorText, t))}
                />
              </div>
              <Button
                size="sm"
                onClick={doAddUserEntry}
                disabled={busy || !editorText.trim()}
                data-testid="resume-studio-editor-add-user-entry-btn"
                className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                <Plus className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Add entry
              </Button>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
